import { useLazyQuery } from "@apollo/client";
import CircularProgress from "@material-ui/core/CircularProgress";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { GET_INSURANCE_POLICIES } from "../../../../graphql/insurancePolicy/getInsurancePolicies";
import {
  DefaultAddons,
  IAddonRate,
  IAddonType,
  ValetType
} from "./types";
import {
  IAddonRequirementInput,
  IAddressLocation,
  IBookingCreateInput,
  IInsuranceAddon,
  IOneWayRentalFee,
  IValetSlotData,
  IVehicleGroupProtectionPlan,
} from "../../../../reducers/bookings/types";
import { CustomCard } from "./CustomCard";
import { InsuranceCard } from "./InsuranceCard";
import styles from './index.module.css';
import Grid from '@material-ui/core/Grid';
import { Container, Hidden, Typography } from '@material-ui/core';
import { GET_AVAILABLE_ADDON_TYPES } from "../../../../graphql/addOns/getAvailableAddOnsQuery";
import { SummaryPanel } from '../../../SummaryPanel';
import { ValetCard } from "./ValetCard";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { ProtectionPlanCard } from "./ProtectionPlanCard";
import { useTranslation } from "react-i18next";
interface IAddonsProps {
  onPrevious(): void;
  values: IBookingCreateInput;
  setValues: Dispatch<SetStateAction<IBookingCreateInput>>
  onSubmit(data?: IBookingCreateInput): void;
  updateParentState(newValue: string): void;
  oneWayRentalFee: IOneWayRentalFee | undefined
  selectedProtectionVehicleGroupPlan: IVehicleGroupProtectionPlan[]
  setLoading: Dispatch<React.SetStateAction<boolean>>
}

export const AddonsSelection: React.FC<IAddonsProps> = (props) => {
  const [activatedAddons, setActivatedAddons] = useState<IAddonType[]>([]);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const [selectedAddons, setSelectedAddons] = useState<
    IAddonRequirementInput[]
  >([]);
  const [insuranceAddOns, setInsuranceAddOns] = useState<IInsuranceAddon[]>([]);
  const authState = useSelector((state: IAppState) => state.authReducer);
  const { hideInsurance } = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { values, setValues } = props;
  const { t } = useTranslation();

  const [loadAddons, { loading: addonsLoading, data: addonsData }] = useLazyQuery(GET_AVAILABLE_ADDON_TYPES, {
    fetchPolicy: "network-only"
  });

  const [loadInsurancePolicies, { loading: insuranceLoading, data: insurancePolicyData }] = useLazyQuery(
    GET_INSURANCE_POLICIES,
    {
      fetchPolicy: "network-only"
    }
  );

  useEffect(() => {
    if (values.branchId) {
      loadInsurancePolicies();
      const vehicleGroupIds = values.vehicleGroups.map((vg)=> vg.vehicleGroup)
      loadAddons({
        variables: {
          branchId: values.branchId,
          startDate: values.pickupDateTime,
          endDate: values.dropoffDateTime,
          bookingId: values.id || "",
          rateTypeName: values.rateTypeName,
          businessCustomer: values.businessCustomer ? values.businessCustomer : authState.user?.businesscustomers?.id,
          vehicleGroupIds
        }
      });
    }
  }, []);
  useEffect(() => {
    if (addonsLoading || insuranceLoading) {
      props.setLoading(true)
    }
    else {
      props.setLoading(false)
    }
  }, [addonsLoading, insuranceLoading])

  // add on data
  useEffect(() => {
    if (addonsData && addonsData.consumerAddonTypes) {
      const selectAddons: IAddonRequirementInput[] = [];
      const applicableAddons = addonsData.consumerAddonTypes.filter(
        (addon: IAddonType) => {
          const addonPresent = values.addonRequirements.find(
            (addReq) => addReq.addon === addon.id
          );
          if (addon.isActivated && addonPresent) {
            selectAddons.push(addonPresent);
          }
          return addon.isActivated;
        }
      );
      if (applicableAddons.length) {
        applicableAddons.sort((a: any, b: any) => {
          const addon1 = a.name || "";
          const addon2 = b.name || "";
          return addon1.toLowerCase() < addon2.toLowerCase()
        })
        const valetIndex = applicableAddons.findIndex((addon: any) => addon.name === DefaultAddons.VALET);
        // If a 'VALET' object exists, move it to the 0 index      
        if (valetIndex && valetIndex !== 0) {
          const valetAddon = applicableAddons.splice(valetIndex, 1)[0];
          applicableAddons.unshift(valetAddon);
        }
        setActivatedAddons(applicableAddons);
        setSelectedAddons(selectAddons);
      }
    }
  }, [addonsData, values.addonRequirements]);

  // insurance policy data
  useEffect(() => {
    if (insurancePolicyData && insurancePolicyData.consumerInsurancePolicies) {
      const insuranceAddOns = [];
      for (
        let index = 0;
        index < insurancePolicyData.consumerInsurancePolicies.length;
        index++
      ) {
        const insurancePolicy = insurancePolicyData.consumerInsurancePolicies[index];
        const insuranceRate = insurancePolicy.insuranceRate.find(
          (iRate: any) => iRate.rateName === values.rateTypeName
        );
        if (insuranceRate) {
          insuranceAddOns.push({
            ...insuranceRate,
            policyId: insurancePolicy.id,
            excess: insurancePolicy.excess,
            policyName: insurancePolicy.name,
            description: insurancePolicy.description,
            isPercentage: insurancePolicy?.isPercentage || false
          });
        }
      }
      setInsuranceAddOns(insuranceAddOns);
    }
  }, [insurancePolicyData]);

  useEffect(() => {

    if (!props.selectedProtectionVehicleGroupPlan.length) {
      return;
    }
    const protectionPlan = props.selectedProtectionVehicleGroupPlan.find(
      (p) => p.plan.id === values.protectionPlan?.plan
    );
    if (protectionPlan) {
      let rate = 0;
      if (protectionPlan.hasFixedRate) {
        rate = protectionPlan.fixedRate!;
      } else if (protectionPlan.recurringRate) {
        const applicableRate = protectionPlan.recurringRate.find((r) =>
          r.rateTypeName === values.rateTypeName
        );
        rate = applicableRate?.rate || 0;
      }
      setValues({
        ...values,
        protectionPlan: {
          plan: protectionPlan.plan.id,
          name: protectionPlan.plan.name,
          hasFixedRate: Boolean(protectionPlan.hasFixedRate),
          taxable: protectionPlan.taxable,
          rate,
          tax: {
            title: protectionPlan.tax.title,
            value: protectionPlan.tax.value,
          },
        },
      });
    } else {
      setValues({
        ...values,
        protectionPlan: undefined,
      });
    }
  }, [props.selectedProtectionVehicleGroupPlan])

  const getAddons = (
    addonType?: IAddonType,
    rate?: number,
    quantity?: number,
    valetType?: string
  ) => {
    if (addonType && addonType.id && quantity) {
      const addons = [...selectedAddons];
      const existingIndex = selectedAddons.findIndex(
        (addon) => addon.addon === addonType.id
      );
      if (existingIndex > -1) {
        addons[existingIndex] = {
          ...addons[existingIndex],
          quantity,
          rate: rate || 0,
        };
      } else {
        // add to selected addons
        if (!valetType || valetType.length == 0) {
          addons.push({
            addon: addonType.id,
            hasFixedRate: addonType.hasFixedRate,
            name: addonType.name,
            quantity,
            displayName: addonType.displayName,
            rate: rate || 0,
            taxable: addonType.taxable,
            tax: addonType.taxable && addonType.tax ? {
              title: addonType.tax.title,
              value: addonType.tax.value
            } : {
              title: "",
              value: 0
            }
          });
        }
        else {
          props.updateParentState(valetType ? valetType : "");
          addons.push({
            displayName: valetType,
            addon: addonType.id,
            hasFixedRate: addonType.hasFixedRate,
            name: addonType.name,
            quantity,
            rate: rate || 0,
            taxable: addonType.taxable,
            tax: addonType.taxable && addonType.tax ? {
              title: addonType.tax.title,
              value: addonType.tax.value
            } : {
              title: "",
              value: 0
            }
          });
        }
      }
      return addons;
    } else {
      return selectedAddons.filter(
        (addon) => addon.addon !== addonType?.id
      );
    }
  };

  const onCustomAddonSelect = (
    addonType?: IAddonType,
    rate?: number,
    quantity?: number,
    additionalDrivers?: number
  ) => {
    const addons = getAddons(addonType, rate, quantity);
    setSelectedAddons(addons);
    setValues({
      ...values,
      addonRequirements: addons
    });
  };

  const onInsuranceAddonSelect = (insuranceAddOn?: string) => {
    setValues((prev) => {
      if (!insuranceAddOn) {
        return {
          ...prev,
          insuranceRate: 0,
          insurancePercentageRate: 0,
          excess: 0,
          insurancePolicy: undefined
        };
      }

      const selected = insuranceAddOns.find(
        ({ policyId }) => policyId === insuranceAddOn
      );

      if (!selected) return prev;

      const isPercentage = selected.isPercentage;
      return {
        ...prev,
        insuranceName: selected.policyName,
        insurancePolicy: selected.policyId,
        excess: selected.excess,
        insuranceRate: isPercentage ? 0 : selected.rate,
        insurancePercentageRate: isPercentage ? selected.rate : 0
      };
    });
  };

  const onProtectionPlanSelect = (protectionPlanId?: string) => {
    if (protectionPlanId) {
      const protectionPlan = props.selectedProtectionVehicleGroupPlan.find(
        (protectionPlan) => protectionPlan.plan.id === protectionPlanId
      );
      if (protectionPlan) {
        let rate = 0;
        if (protectionPlan.hasFixedRate) {
          rate = protectionPlan.fixedRate!;
        } else if (protectionPlan.recurringRate) {
          const applicableRate = protectionPlan.recurringRate.find(rate =>
            rate.rateTypeName == props.values.rateTypeName
          );
          rate = applicableRate?.rate || 0;
        }
        setValues({
          ...values,
          protectionPlan: {
            plan: protectionPlan.plan.id,
            name: protectionPlan.plan.name,
            hasFixedRate: Boolean(protectionPlan.hasFixedRate),
            taxable: protectionPlan.taxable,
            rate: rate,
            tax: {
              title: protectionPlan.tax.title,
              value: protectionPlan.tax.value,
            }
          }
        });
      }
    } else {
      setValues({
        ...values,
        protectionPlan: undefined
      });
    }
  };
  const isProtectionPlanSet = (plan: string) => {
    const protectionPlan = props.selectedProtectionVehicleGroupPlan.find(
      (protectionPlan) => protectionPlan.plan.id === plan
    );
    return protectionPlan !== undefined
  }

  const shouldShowProtectionPlan = props.selectedProtectionVehicleGroupPlan.some((plan) => {
    if (plan.hasFixedRate) return (plan.fixedRate ?? 0) > 0;
    if (plan.recurringRate?.length) {
      const applicable = plan.recurringRate.find((r) => r.rateTypeName === values.rateTypeName);
      return (applicable?.rate ?? 0) > 0;
    }
    return false;
  });

  const onCoiSelect = (flag: boolean) => {
    if (flag) {
      setValues({
        ...values,
        isCoi: true,
        insurancePolicy: "",
        insuranceRate: 0
      })
    } else {
      setValues({
        ...values,
        isCoi: false
      })
    }
  }

  const onValetSelect = (args: {
    addonType: IAddonType,
    quantity: number,
    rate?: number,
    isNonNegotiable?: boolean,
    pickupOtherLocation?: IAddressLocation,
    dropoffOtherLocation?: IAddressLocation,
    valetSlot: IValetSlotData
  }) => {
    const {
      addonType,
      rate,
      quantity,
      pickupOtherLocation,
      dropoffOtherLocation,
      isNonNegotiable,
      valetSlot
    } = args;
    const addons = getAddons(addonType, rate, quantity);

    if (addonType && (pickupOtherLocation?.fullAddress || dropoffOtherLocation?.fullAddress)) {
      setSelectedAddons(addons);
      setValues({
        ...values,
        addonRequirements: addons,
        pickupOtherLocation,
        dropoffOtherLocation,
        isNonNegotiable,
        valetSlot: valetSlot
      });
    } else {
      const { pickupOtherLocation, dropoffOtherLocation, ...rest } = values;
      setSelectedAddons(addons);
      setValues({
        ...rest,
        addonRequirements: addons,
        isNonNegotiable,
        valetSlot: valetSlot || {
          deliverySlot: { slot: "", driverId: "" },
          collectionSlot: { slot: "", driverId: "" }
        }
      });
    }
  };


  if (addonsLoading || insuranceLoading) {
    return <div style={{ display: "flex", justifyContent: "center" }}>
      <CircularProgress />
    </div>
  }
  return (
    <Container>
      <Grid container spacing={0}>
        <Grid item xs={12} md={8}>
          <Typography variant="h4" align="center" style={{ fontWeight: 700, marginBottom: 20 }} >{t("root:selectAddons")}</Typography>
          {
            !insuranceAddOns.length && !activatedAddons.length && (
              <Grid container item xs={12}>
                <Typography variant="h4">{t("root:noAvailableAddonsFound")}</Typography>
              </Grid>
            )
          }
          {
            <Grid container spacing={1} style={{ paddingBottom: 100 }}>
              {shouldShowProtectionPlan && (
                <Grid item xs={12} md={12}>
                  <div
                    className={styles.insuranceAddonCard}
                    style={
                      values.protectionPlan?.plan &&
                        isProtectionPlanSet(values.protectionPlan.plan)
                        ? {
                          borderColor: "var(--primary-color)",
                          backgroundColor: "var(--accent-shade-light)",
                        }
                        : undefined
                    }
                  >
                    <ProtectionPlanCard
                      bookingData={values}
                      selectedProtectionVehicleGroupPlan={
                        props.selectedProtectionVehicleGroupPlan
                      }
                      onSelect={onProtectionPlanSelect}
                      selectedProtectionPlan={values.protectionPlan?.plan}
                    />
                  </div>
                </Grid>
              )}
              {!hideInsurance && !user?.businesscustomers?.coiDetails && insuranceAddOns.length > 0 && (
                <Grid item xs={12} md={6}>
                  <div
                    className={styles.insuranceAddonCard}
                    style={values.insurancePolicy ? {
                      borderColor: "var(--primary-color)",
                      backgroundColor: 'var(--accent-shade-light)'
                    } : {}}
                  >
                    <InsuranceCard
                      insuranceAddOns={insuranceAddOns}
                      onSelect={onInsuranceAddonSelect}
                      selectedAddon={values.insurancePolicy}
                      onCoiSelect={onCoiSelect}
                      isCoi={values.isCoi}
                    />
                  </div>
                </Grid>
              )}
              {activatedAddons.map((addonType: IAddonType, index: number) => {
                const selectedAddon = selectedAddons.find(
                  (ao) => ao.addon === addonType.id
                );
                if (addonType.name === DefaultAddons.VALET) {
                  return (
                    <Grid item xs={12} md={6} key={index}>
                      <ValetCard
                        addon={addonType}
                        bookingData={values}
                        onSelect={onValetSelect}
                        selectedAddon={selectedAddon}
                      />
                    </Grid>
                  );
                }
                else if (addonType.name === DefaultAddons.ADDITIONAL_DRIVER) {
                  return (
                    <Grid item xs={12} sm={6} md={4}>
                      <div
                        className={styles.customAddonCard}
                        style={addonType.name === selectedAddon?.name ? {
                          borderColor: "var(--primary-color)",
                          backgroundColor: 'var(--accent-shade-light)'
                        } : {}}
                      >
                        <CustomCard
                          addon={addonType}
                          rateTypeName={props.values.rateTypeName}
                          onSelect={onCustomAddonSelect}
                          selectedAddon={selectedAddon}
                        />
                      </div>
                    </Grid>
                  )
                }
                else {
                  if (addonType.availableQuantity) {
                    return (
                      <Grid item xs={12} sm={6} md={4}>
                        <div
                          className={styles.customAddonCard}
                          style={addonType.name === selectedAddon?.name ? {
                            borderColor: "var(--primary-color)",
                            backgroundColor: 'var(--accent-shade-light)'
                          } : {}}
                        >
                          <CustomCard
                            addon={addonType}
                            rateTypeName={props.values.rateTypeName}
                            onSelect={onCustomAddonSelect}
                            selectedAddon={selectedAddon}
                          />
                        </div>
                      </Grid>
                    );
                  }
                }
              })}
            </Grid>
          }
        </Grid>
        <Hidden mdDown>
          <Grid item xs={12} lg={1}></Grid>
        </Hidden>
        <Grid item xs={12} md={4} lg={3}>
          <SummaryPanel bookingInput={values} oneWayRentalFee={props.oneWayRentalFee ? props.oneWayRentalFee : undefined} />
        </Grid>
      </Grid>
    </Container >
  );
};
